/* eslint-disable camelcase */
import React from 'react';
import { Link } from 'react-router-dom';
import Tooltip from '@material-ui/core/Tooltip';
import { StyleSheet, css } from 'aphrodite';

import moment from 'moment-timezone'
import { ApiHelper } from '../helpers/ApiHelper';
import { AppStyles, AppTheme } from '../../styles/AppTheme';
import { Helper } from '../helpers/Helper';
import { AppHelper } from '../../helpers/AppHelper';
import { BaseEntityPage } from './BaseEntityPage';
import { AppConfig } from '../../AppConfig';
import TableCard from './tables/TableCard';
import { FormControl, Grid, TableCell, TextField } from '@material-ui/core';
import { StatCard } from './StatCard';
import { CRMHelper } from '../../helpers/CRMHelper';
import { CommonTheme } from '../../styles/Styles';
import { ReactDateRangeWrapper } from './DateRangeComponent/ReactDateRangeWrapper';
import { FilterHelper } from '@slx/slixta-common';
import { UiHelper } from '../helpers/UiHelper';
import { DateRanges } from './DateRangeComponent/DateRanges';
import { PageDialog } from './PageDialog';
import { UserActivity2 } from './UserActivity2';
import { Controller } from '../Controller';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { SearchBar } from './SearchBar';
import { StaffHelper } from '../helpers/StaffHelper';

const FIXED_REPORT_HEADERS = ['STAFF', 'TOTAL', 'CONTACTS', 'NEW CONTACTS', 'VALUE']

function generateStatsData(staff, orderedKeys = [], workflows, isInteractionFiltered) {
  let lookup = {}
  if((staff && staff.activity && staff.activity.length > 0)) {
    lookup = staff.activity.reduce((acc, item) => {
      acc[item.key] = item.doc_count
      return acc
    }, {})
  }

  const statsData = {}
  statsData.TOTAL = isInteractionFiltered ? orderedKeys.reduce((sum, key) => sum + (lookup[key] || 0), 0) : (staff.doc_count || 0)
  statsData.CONTACTS = staff.uniqueContacts || 0
  statsData['NEW CONTACTS'] = staff.uniqueNewContacts || 0
  statsData.VALUE = staff.paymentValue || 0

  orderedKeys.forEach((key) => {
    let formattedKey = formatInteractionName(key)
    if(workflows && typeof workflows === 'object' && workflows[key] && typeof workflows[key] === 'object' && workflows[key].title) {
      formattedKey = workflows[key].title
    }

    if(!FIXED_REPORT_HEADERS.includes(formattedKey)) {
      if(statsData[formattedKey]) {
        statsData[formattedKey] = statsData[formattedKey] + (lookup[key] || 0)
      } else {
        statsData[formattedKey] = lookup[key] || 0
      }
    }
  })

  return statsData
}

function resolveInteractionHeader(columnKey, workflows, interactionHeaders) {
  if (!columnKey) {
    return null
  }
  if (interactionHeaders.includes(columnKey)) {
    return columnKey
  }
  if (workflows && typeof workflows === 'object' && workflows[columnKey] && typeof workflows[columnKey] === 'object' && workflows[columnKey].title) {
    const title = workflows[columnKey].title
    if (interactionHeaders.includes(title)) {
      return title
    }
  }
  const formatted = formatInteractionName(columnKey)
  if (interactionHeaders.includes(formatted)) {
    return formatted
  }
  return null
}

function generateStaffActivityReport({staffReport, stats, orgId, openUserActivityDialog, workflows, isInteractionFiltered}) {
  if(!staffReport || staffReport.length === 0) {
    return {}
  }

  let keys = []
  let reportHeaderKeys = []

  if(stats && stats.length > 0) {
    keys = stats.map(item => {
      if(item && item.key) {
        return item.key
      }
    }).filter(a => !!a)

    reportHeaderKeys = stats.map(item => {
      if(item && item.key) {
        if(workflows && typeof workflows === 'object' && workflows[item.key] && typeof workflows[item.key] === 'object' && workflows[item.key].title) {
          return workflows[item.key].title
        } else {
          return formatInteractionName(item.key)
        }
      }
    }).filter(a => !!a)
  }

  const interactionHeaders = reportHeaderKeys
  const configuredColumnsSetting = AppHelper.getSetting(orgId, 'STAFF_INTERACTIONS_REPORT_COLUMNS')
  const configuredColumns = configuredColumnsSetting ? configuredColumnsSetting.split(',').map(item => item.trim()).filter(Boolean) : []
  let orderedInteractions = interactionHeaders
  if (configuredColumns.length > 0) {
    const prioritized = []
    const prioritizedSet = new Set()
    const fixedHeaders = new Set(FIXED_REPORT_HEADERS)
    configuredColumns.forEach((col) => {
      if (fixedHeaders.has(col) || fixedHeaders.has(formatInteractionName(col))) {
        return
      }
      const header = resolveInteractionHeader(col, workflows, interactionHeaders)
      if (header && !prioritizedSet.has(header)) {
        prioritized.push(header)
        prioritizedSet.add(header)
      }
    })
    orderedInteractions = [...prioritized, ...interactionHeaders.filter(header => !prioritizedSet.has(header))]
  }
  const reportHeaders = [...FIXED_REPORT_HEADERS, ...orderedInteractions]
  const hiddenColumns = []
  const textColumnIndices = [0]
  const stickyColumnIndices = [0]

  let data = []

  if(reportHeaderKeys && reportHeaderKeys.length > 0) {
    data = staffReport.map((staff)=>{
      if(staff) {
        return({
          data: {
            userId: staff.userId,
            name: staff.name,
            email: staff.email,
            phone: staff.phone,
          },
          ...generateStatsData(staff, keys, workflows, isInteractionFiltered),
        })
      }
    }).filter(a => !!a)
  }

  return {
    headers: reportHeaders,
    rows: data.map(item => reportHeaders.map(header => header === 'STAFF' ? item.data : (item[header] != null ? item[header] : 0))),
    textColumnIndices: textColumnIndices,
    hiddenColumnIndices: hiddenColumns,
    stickyColumnIndices: stickyColumnIndices,
    renderCustomTableBody: (cellValue, columnIndex) => {
      if(columnIndex === 0) {
        const isStaffActivityTrackingEnabled = AppHelper.isStaffActivityTrackingEnabled(orgId)
        return(
          <TableCell key={columnIndex} className={css(Styles.cellBody, Styles.firstColumn, Styles.cellWidthNormal, (stickyColumnIndices.includes(columnIndex) && Styles.stickyColumn))}>
            <Tooltip
              title={<div className={css(Styles.spanValue, Styles.infoCell, Styles.tooltip)}>
                {cellValue && cellValue.name && <span className={css(Styles.infoFieldTooltip)}><span className={css(Styles.infoFieldLabel)}>{'Name:'}</span>{cellValue.name}</span>}
                {cellValue && cellValue.email && <span className={css(Styles.infoFieldTooltip)}><span className={css(Styles.infoFieldLabel)}>{'Email:'}</span>{cellValue.email}</span>}
                {cellValue && cellValue.phone && (<span className={css(Styles.infoFieldTooltip)}><span className={css(Styles.infoFieldLabel)}>{'Phone:'}</span>{cellValue.phone}</span>)}
              </div>}
              placement='top'
              className={css(Styles.tooltip)}
            >
              <div className={css(Styles.spanValue, Styles.infoCell)}>
                {cellValue && (cellValue.name || cellValue.email || cellValue.phone) && <span className={css(Styles.infoField, isStaffActivityTrackingEnabled && Styles.infoFieldHyperLink)} onClick={(e) => {
                  if(isStaffActivityTrackingEnabled) {
                    e.stopPropagation()
                    openUserActivityDialog(cellValue)
                  }
                }}>{cellValue.name || cellValue.email || cellValue.phone}</span>}
              </div>
            </Tooltip>
          </TableCell>
        )
      }
      return null
    }
  }
}

function formatInteractionName(str = '') {
  return str
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-\s]+/g, ' ')
    .trim()
    .toUpperCase()
}

function buildInteractionOptions(statsActivity, workflows) {
  const interactions = [{ key: 'all', label: 'All' }]
  if(statsActivity && statsActivity.length > 0) {
    for (const item of statsActivity) {
      if(item && item.key) {
        let label = formatInteractionName(item.key)
        if(workflows && typeof workflows === 'object' && workflows[item.key]
          && typeof workflows[item.key] === 'object' && workflows[item.key].title) {
          label = workflows[item.key].title
        }
        interactions.push({
          key: item.key,
          label,
        })
      }
    }
  }
  return interactions
}

function getMonthlyExportFieldDef() {
  function getExportMonths() {
    const MAX_MONTHS = 12
    const result = [];
    for (let i = 0; i < MAX_MONTHS; i++) {
      const m = moment().subtract(i, 'months');
      result.push({
        label: m.format('MMMM, YYYY'),
        key: m.format('MMYYYY')
      });
    }
    return result;
  }
  const last3Months = getExportMonths()
  const fieldDef = {
    title: 'Export',
    ctaButtonText: 'Export',
    steps: [
      {
        fields: [
          {
            key: 'mmyyyy',
            type: 'radio',
            label: 'Month',
            tip: 'Choose the month in which staff activity occurred',
            options: last3Months,
            v_required: {
              value: true,
              err: 'Choose a month'
            },
            placement: 'left',
            defaultValue: last3Months[0].key,
          },
        ]
      }
    ]
  }
  return fieldDef
}

function getTableExportFieldDef(options) {
  const {orgId, timeFrame, teamSelected} = options
  function _getField(key, label, currentVal, currentLabel, placement, attribs = {}) {
    return {
      key,
      type: 'radio',
      label,
      options: [{key: currentVal, label: currentLabel}],
      v_required: {
        value: true,
        err: `Choose ${label}`,
      },
      placement,
      defaultValue: currentVal,
      ...attribs,
    }
  }
  const dateRange = DateRanges.find(dr => dr.id === timeFrame.id)
  const dateRangeLabel = timeFrame.id === 'custom' ? `${moment(timeFrame.startValue).format('YYYY-MM-DD')} to ${moment(timeFrame.endValue).format('YYYY-MM-DD')}` : dateRange.label
  const timeFrameId = timeFrame.id
  const startTs = timeFrame.startValue
  const endTs = timeFrame.endValue

  let orgUnitId = orgId
  let teamLabel = 'All'
  if(teamSelected && teamSelected.key !== 'all') {
    orgUnitId = teamSelected.key
    teamLabel = teamSelected.label
  }

  const fieldDef = {
    title: Helper.getString('exportSummary'),
    ctaButtonText: 'Export',
    steps: [
      {
        fields: [
          _getField('dateFilter', 'Date', timeFrameId, dateRangeLabel, 'left'),
          _getField('orgUnitId', 'Team', orgUnitId, teamLabel, 'right'),
          _getField('startTs', 'Start Date', startTs, startTs, 'left', {hidden: true, v_required: {value: false}}),
          _getField('endTs', 'End Date', endTs, endTs, 'left', {hidden: true, v_required: {value: false}}),
        ]
      }
    ],
    disableAlwaysFormFields: ['dateFilter', 'orgUnitId', 'dateFrom', 'dateTo'],
  }
  return fieldDef
}

function getTimeFrameData(timeFrame) {
  return {
    id: timeFrame.id,
    startValue: moment(timeFrame.startDate).startOf('day').valueOf(),
    endValue: moment(timeFrame.endDate).endOf('day').valueOf()
  }
}

function processActivityStats(stats, workflows) {
  let processedStats = []

  if(stats && stats.length > 0) {
    const formattedStats =  []
    stats.forEach(item => {
      if(item && item.key) {
        const formattedStatsObj = {}
        if(workflows && typeof workflows === 'object' && workflows[item.key] && typeof workflows[item.key] === 'object' && workflows[item.key].title) {
          formattedStatsObj.key = workflows[item.key].title
        } else {
          formattedStatsObj.key = formatInteractionName(item.key)
        }
        formattedStatsObj.doc_count = item.doc_count
        formattedStats.push(formattedStatsObj)
      }
    })

    const grouped = formattedStats.reduce((acc, curr) => {
      if (acc[curr.key]) {
        acc[curr.key] += curr.doc_count
      } else {
        acc[curr.key] = curr.doc_count
      }
      return acc
    }, {})

    processedStats = Object.entries(grouped).map(([key, doc_count]) => ({ key, doc_count }))
  }
  return processedStats
}

export class StaffActivity2 extends BaseEntityPage {

  constructor(props) {
    super(props)
    this.orgId = this.props.match.params.orgId || 'PRODUCT'
    this.subOrgId = Controller.get().userMgr().getSubOrgId(this.orgId)
    this.teamsEnabled = AppHelper.isTeamsEnabled(this.orgId)
    this.pageTitle = Helper.getString('staff-activity')
    this.pageKey = 'staff-activity'
    this.breadCrumbs = [
      { title: Helper.getString('home'), to: this.baseRoute() }
    ]
    this.breadCrumbs.push({ title: this.pageTitle })
    this.timezone = Helper.getLocalTimezone()
    this.timeFrameKey = 'this_month'
    this.timeFrame = getTimeFrameData({id: this.timeFrameKey, startDate: FilterHelper.getEpochFromDateId(this.timeFrameKey, 'startDate', this.timezone), endDate: FilterHelper.getEpochFromDateId(this.timeFrameKey, 'endDate', this.timezone)})
    this.toFetch = [
      { key: 'staffReport', ms: AppConfig.CDP_MS.ENDPOINT, method: 'GET', paths: ['miscs', 'get-staff-report'], queryParams: { orgId: this.orgId, ...(this.subOrgId && {subOrgId: this.subOrgId}), orgUnitId: this.orgId, startTs: this.timeFrame.startValue, endTs: this.timeFrame.endValue } },
      { key: 'workflows', ms: AppConfig.CDP_MS.ENDPOINT, method: 'GET', paths: ['miscs', 'get-workflows'], queryParams: { orgId: this.orgId} },
    ]
    if (this.teamsEnabled) {
      this.toFetch.push({ key: 'suborgs', ms: AppConfig.CDP_MS.ENDPOINT, method: 'GET', paths: ['miscs', 'get-sub-orgs'], queryParams: {orgId: this.orgId, parentOrgId: this.orgId } })
    }
    this.headerActions = [
      {
        label: Helper.getString('exportActivity'),
        tooltip: Helper.getString('exportActivity'),
        icon: 'download',
        type: 'icon-label',
        onClick: () => {
          const apiData = {
            monthlyExport: true
          }
          const fieldDef = getMonthlyExportFieldDef()
          fieldDef.title = Helper.getString('exportActivity')
          this.openModalForAdd(fieldDef,apiData)
        }
      },
      {
        label: Helper.getString('exportSummary'),
        tooltip: Helper.getString('exportSummary'),
        icon: 'download',
        type: 'icon-label',
        onClick: () => {
          const apiData = {
            tableExport: true
          }
          const fieldDef = getTableExportFieldDef({orgId: this.orgId, timeFrame: this.state.timeFrame, teamSelected: this.state.teamSelected})
          fieldDef.title = Helper.getString('exportSummary')
          this.openModalForAdd(fieldDef, apiData)
        }
      }
    ]


    this.emptyText = 'No staff in this org yet!'
    this.apiData = {}
    this.teams = [
      {key: 'all', label: 'All'},
    ]
    this.team = this.teams[0]
    this.interactions = [
      {key: 'all', label: 'All'},
    ]
    this.interaction = this.interactions[0]
    this.state.searchText = ''
  }

  onFetchSuccess(results) {
    this.fieldDef = results[0].fieldDef
    this.formDefinition = this.fieldDef
    this.workflows = this.getResult(results, 'workflows') && this.getResult(results, 'workflows').workflows

    const teams = [...this.teams]
    if (this.teamsEnabled) {
      this.subOrgs = this.getResult(results, 'suborgs') ? this.getResult(results, 'suborgs').items : []
      if(this.subOrgs && this.subOrgs.length > 0) {
        for (const d of this.subOrgs) {
          teams.push({
            key: d.id || d.userId,
            label: d.title
          })
        }
      }
    }

    const staffReport = this.getResult(results, 'staffReport')
    const interactions = buildInteractionOptions(staffReport && staffReport.statsActivity, this.workflows)

    this.setState({
      timeFrame: this.timeFrame,
      statsStaffUser: staffReport && staffReport.statsStaffUser,
      statsStaffUserMasterList: staffReport && staffReport.statsStaffUser,
      statsActivity: staffReport && staffReport.statsActivity,
      statsActivityMasterList: staffReport && staffReport.statsActivity,
      totalPaymentValue: staffReport && staffReport.totalPaymentValue != null ? staffReport.totalPaymentValue || 0 : null,
      totalUniqueContacts: staffReport && staffReport.totalUniqueContacts || 0,
      totalUniqueNewContacts: staffReport && staffReport.totalUniqueNewContacts || 0,
      teamSelected: (this.teams && this.teams.length > 0 ) && this.teams.find(option => option.key === this.team.key),
      teams: teams,
      interactionSelected: (this.interactions && this.interactions.length > 0 ) && this.interactions.find(option => option.key === this.interaction.key),
      interactions: interactions,
    })
  }

  openModalForAdd(fieldDef, apiData = {}) {
    this.apiData = {
      orgId: this.orgId,
      ...apiData,
    }

    let fieldDefinition = null
    if(fieldDef) {
      fieldDefinition = JSON.parse(JSON.stringify(fieldDef))
    }

    super.openModalForAdd(fieldDefinition)
  }

  renderAggregatedStats() {
    if(Array.isArray(this.state.statsActivity) && this.state.statsActivity.length > 0) {
      let cardsData = []
      const totalCount = this.state.statsActivity.reduce((sum, item) => sum + item.doc_count, 0)
      cardsData.push({
        key: 'total_interactions',
        text: totalCount || 0,
        subText: 'TOTAL INTERACTIONS',
        cardIndex: 1,
        cardId: 'total_interactions',
        padding: 'small'
      })

      cardsData.push({
        key: 'unique_contacts',
        text: this.state.totalUniqueContacts || 0,
        subText: 'UNIQUE CONTACTS',
        cardIndex: 2,
        cardId: 'unique_contacts',
        padding: 'small'
      })

      cardsData.push({
        key: 'unique_new_contacts',
        text: this.state.totalUniqueNewContacts || 0,
        subText: 'NEW CONTACTS',
        cardIndex: 3,
        cardId: 'unique_new_contacts',
        padding: 'small'
      })

      cardsData.push({
        key: 'total_value',
        text: this.state.totalPaymentValue || 0,
        subText: 'TOTAL VALUE',
        cardIndex: 4,
        cardId: 'total_value',
        padding: 'small'
      })

      cardsData.push({
        key: 'total_staff',
        text: Array.isArray(this.state.statsStaffUserMasterList) ? this.state.statsStaffUserMasterList.length : 0,
        subText: 'TOTAL STAFF',
        cardIndex: 5,
        cardId: 'total_staff',
        padding: 'small'
      })

      const statsActivity = processActivityStats(this.state.statsActivity, this.workflows)

      const cardIndexOffset = 6
      statsActivity.forEach((card, i) => {
        cardsData.push({
          key: card.key,
          text: card.doc_count || 0,
          subText: formatInteractionName(card.key),
          cardIndex: i + cardIndexOffset,
          cardId: card.key,
          padding: 'small'
        })
      })

      const maxCardsPerRow = 7
      cardsData = CRMHelper.splitCardsIntoBalancedRows(cardsData, maxCardsPerRow)
      if(cardsData && cardsData.length > 0) {
        return(<div className={css(Styles.tableStatItem)}>
          {((!this.isMobileView) && cardsData && cardsData.length > 0) && (<div className={css(Styles.numericStatItemRow)}>
            {cardsData.map((row)=>(<Grid container className={css(Styles.numericStatContainerRow)}>
              {row.map((card)=>(
                <StatCard
                  xs={4}
                  sm={1}
                  key={card.key}
                  text={card.text}
                  subText={card.subText}
                  cardIndex={card.cardIndex}
                  padding={card.padding}
                  cardId={card.cardId}
                  handleCardClick={card.handleCardClick}
                  containerStyle={Styles.statsCardContainer}
                  cardStyle={Styles.statsCard}
                />))}
            </Grid>))}
          </div>)}
          {((this.isMobileView)) && cardsData && cardsData.length > 0 && (<div className={css(Styles.numericStatItemRow)}>
            {cardsData.map((row, index) => {
              const rowGroups = Array.from({ length: Math.ceil(row.length / 3) })
              return (
                <Grid key={index}>
                  {rowGroups.map((_, groupIndex) => {
                    const groupItems = row.slice(groupIndex * 3, groupIndex * 3 + 3)
                    return (
                      <Grid
                        key={groupIndex}
                        container
                        className={css(Styles.numericStatContainerRow)}
                      >
                        {groupItems.map((card) => (
                          <StatCard
                            xs={12 / groupItems.length}
                            sm={12 / groupItems.length}
                            key={card.key}
                            text={card.text}
                            subText={card.subText}
                            cardIndex={card.cardIndex}
                            padding={card.padding}
                            cardId={card.cardId}
                            handleCardClick={card.handleCardClick}
                            containerStyle={Styles.statsCardContainer}
                            cardStyle={Styles.statsCard}
                          />
                        ))}
                      </Grid>
                    )
                  })}
                </Grid>
              )
            })}
          </div>)}
        </div>)
      }
    }
    return null
  }

  openUserActivityDialog(current) {
    if(current) {
      const timeFrame = this.state.timeFrame || this.timeFrame
      let orgUnitId = this.orgId
      let teamLabel = 'All'
      if(this.state.teamSelected && this.state.teamSelected.key !== 'all') {
        orgUnitId = this.state.teamSelected.key
        teamLabel = this.state.teamSelected.label || this.state.teamSelected.key
      }

      let subActivityType = null
      let interactionLabel = 'All'
      if(this.state.interactionSelected && this.state.interactionSelected.key !== 'all') {
        subActivityType = this.state.interactionSelected.key
        interactionLabel = this.state.interactionSelected.label || this.state.interactionSelected.key
      }

      const dateRange = DateRanges.find(dr => dr.id === timeFrame.id)
      const dateLabel = timeFrame.id === 'custom'
        ? `${moment(timeFrame.startValue).format('YYYY-MM-DD')} to ${moment(timeFrame.endValue).format('YYYY-MM-DD')}`
        : (dateRange && dateRange.label) || timeFrame.id

      const filterSummary = {
        date: dateLabel,
      }
      if(this.teamsEnabled) {
        filterSummary.team = teamLabel
      }
      if(AppHelper.isStaffActivityInteractionFilterEnabled(this.orgId)) {
        filterSummary.interaction = interactionLabel
      }

      this.setState(prevState => {
        const newState = JSON.parse(JSON.stringify(prevState));
        newState.entityDialogProps = {
          match: {params: {orgId: this.orgId, subOrgId: this.subOrgId, userId: current.userId}},
          hideBreadCrumbs: true,
          isLogoForMobileHidden: true,
          isHeaderTitleHidden: true,
          hideHeaderbar: true,
          pageTitle: current.name,
          timeFrame,
          orgUnitId,
          subActivityType,
          filterSummary,
        }
        newState.entityId = current.userId
        newState.isEntityDialogOpen = true
        newState.entityDialogFullHeight = !this.isMobileView && true
        newState.entityDialogFullWidth = true
        newState.entityDialogTitle = current.name
        return newState
      })
    }
  }

  async reFetchReport({timeFrame = this.timeFrame, orgUnitId = this.orgId, interactionId = 'all'}) {
    this.setState({
      fetchState: ApiHelper.State.LOADING,
      loadingMode: ApiHelper.LOADING_MODE.INSTANT_LOAD,
      customError: null,
    })

    if(timeFrame) {
      ApiHelper.callAwait({
        method: 'GET',
        endPoint: ApiHelper.makeUrlPath2({
          ms: AppConfig.CDP_MS.ENDPOINT,
          paths: ['miscs', 'get-staff-report'],
          queryParams: {orgId: this.orgId, ...(this.subOrgId && {subOrgId: this.subOrgId}), orgUnitId, startTs: timeFrame.startValue, endTs: timeFrame.endValue },
        })
      }).then(response => {
        const statsActivityMasterList = response && response.statsActivity
        const interactions = buildInteractionOptions(statsActivityMasterList, this.workflows)
        const interactionStillPresent = interactionId === 'all' || interactions.some(option => option.key === interactionId)
        const selectedInteractionId = interactionStillPresent ? interactionId : 'all'
        let statsActivity = statsActivityMasterList
        if(selectedInteractionId !== 'all' && statsActivityMasterList && statsActivityMasterList.length > 0) {
          statsActivity = statsActivityMasterList.filter(item => item.key === selectedInteractionId)
        }

        const statsStaffUserMasterList = response && response.statsStaffUser
        let statsStaffUser = statsStaffUserMasterList
        if(this.state.searchText) {
          statsStaffUser = StaffHelper.searchOnUserList(statsStaffUserMasterList, this.state.searchText)
        }

        if(response) {
          this.setState({
            timeFrame: timeFrame,
            statsStaffUser: statsStaffUser,
            statsStaffUserMasterList: statsStaffUserMasterList,
            statsActivity: statsActivity,
            statsActivityMasterList: statsActivityMasterList,
            totalPaymentValue: response.totalPaymentValue != null ? response.totalPaymentValue || 0 : null,
            totalUniqueContacts: response.totalUniqueContacts || 0,
            totalUniqueNewContacts: response.totalUniqueNewContacts || 0,
            interactionSelected: interactions.find(option => option.key === selectedInteractionId) || interactions[0],
            interactions: interactions,
            fetchState: ApiHelper.State.READY,
            loadingMode: ApiHelper.LOADING_MODE.PAGE_LOAD
          })
        }
      }).catch(err => {
        const errorMessage = Helper.getErrorMsg(err);
        this.setState({
          customError: errorMessage,
          fetchState: ApiHelper.State.READY,
          loadingMode: ApiHelper.LOADING_MODE.PAGE_LOAD
        })
      })
    } else {
      this.setState({
        customError: null,
        fetchState: ApiHelper.State.READY,
        loadingMode: ApiHelper.LOADING_MODE.PAGE_LOAD
      })
    }
  }

  onDateRangeChange(dateRangeValues) {
    const timeFrame = getTimeFrameData(dateRangeValues)
    let orgUnitId = this.orgId
    if(this.state.teamSelected && this.state.teamSelected.key !== 'all') {
      orgUnitId = this.state.teamSelected.key
    }

    let interactionId = 'all'
    if(this.state.interactionSelected && this.state.interactionSelected.key !== 'all') {
      interactionId = this.state.interactionSelected.key
    }

    this.reFetchReport({timeFrame: timeFrame, orgUnitId: orgUnitId, interactionId: interactionId})
  }

  onTeamsAutoCompleteChange = (value) => {
    if(value && value.key) {
      let interactionId = 'all'
      if(this.state.interactionSelected && this.state.interactionSelected.key !== 'all') {
        interactionId = this.state.interactionSelected.key
      }
      this.setState({
        teamSelected: value,
      }, ()=> {
        this.reFetchReport({timeFrame: this.state.timeFrame, orgUnitId: value.key === 'all' ? this.orgId : value.key, interactionId: interactionId})
      })
    }
  }

  onInteractionsAutoCompleteChange = (value) => {
    if(value && value.key) {
      let statsActivity = this.state.statsActivityMasterList
      if(value.key !== 'all') {
        if(statsActivity && statsActivity.length > 0) {
          statsActivity = statsActivity.filter(item => item.key === value.key)
        }
      }

      this.setState({
        interactionSelected: value,
        statsActivity: statsActivity
      })
    }
  }

  renderFilters() {
    const availableDateRanges = ['today', 'yesterday', 'this_week', 'this_month', 'last_7d', 'last_30d', 'last_90d', 'last_6m', 'last_12m', 'current_7d', 'current_30d', 'custom']

    if(this.state.timeFrame) {
      this.dateRangeOptions = {
        id: this.state.timeFrame.id,
        startDate: new Date(this.state.timeFrame.startValue),
        endDate: new Date(this.state.timeFrame.endValue)
      }
    } else {
      const defaultTimeframe = this.timeFrame
      this.dateRangeOptions = {
        id: defaultTimeframe.id,
        startDate: new Date(defaultTimeframe.startValue),
        endDate: new Date(defaultTimeframe.endValue)
      }
    }

    return (<div className={css(Styles.filterContainer)}>
      <div className={css(Styles.formControlContainer)}>
        <p className={css(Styles.label)}>{'Date'}</p>
        <FormControl className={css(Styles.formControlMainFilters)} variant='outlined'>
          <ReactDateRangeWrapper
            onDateRangeChange={this.onDateRangeChange.bind(this)}
            initialRange={this.dateRangeOptions}
            availableDateRanges = {availableDateRanges}
            timezone={this.timezone}
            displayFieldStyle={Styles.displayFieldStyle}
          />
        </FormControl>
      </div>
      {this.teamsEnabled && <div className={css(Styles.formControlContainer)}>
        <p className={css(Styles.label)}>{'Team'}</p>
        <FormControl className={css(Styles.formControlMainFilters)} variant='outlined'>
          <Autocomplete
            value={this.state.teamSelected || this.team}
            options={this.state.teams || this.teams}
            onChange={(e, val) => this.onTeamsAutoCompleteChange(val)}
            getOptionLabel={(option) => option.label || option.text}
            getOptionSelected={(option, value) => option.key === value.key}
            disableClearable
            forcePopupIcon={this.state.teams.length > 1}
            popupIcon={this.state.teams.length > 1 ? undefined : null}
            open={this.state.teams.length > 1 ? undefined : false}
            renderInput={params =>
              <TextField
                {...params}
                variant={'standard'}
                className={css(Styles.formControlFilters)}
                InputProps={{
                  ...params.InputProps,
                  disableUnderline: true,
                  readOnly: true,
                  classes: {
                    root: css([Styles.textInputStyles, Styles.autoCompleteInputRoot]),
                    input: css(Styles.formInputStyle, Styles.textInput),
                  },
                }} />}
          />
        </FormControl>
      </div>}
      {AppHelper.isStaffActivityInteractionFilterEnabled(this.orgId) && <div className={css(Styles.formControlContainer)}>
        <p className={css(Styles.label)}>{'Interaction'}</p>
        <FormControl className={css(Styles.formControlMainFilters)} variant='outlined'>
          <Autocomplete
            value={this.state.interactionSelected || this.interaction}
            options={this.state.interactions || this.interactions}
            onChange={(e, val) => this.onInteractionsAutoCompleteChange(val)}
            getOptionLabel={(option) => option.label || option.text}
            getOptionSelected={(option, value) => option.key === value.key}
            disableClearable
            forcePopupIcon={this.state.interactions.length > 1}
            popupIcon={this.state.interactions.length > 1 ? undefined : null}
            open={this.state.interactions.length > 1 ? undefined : false}
            renderInput={params =>
              <TextField
                {...params}
                variant={'standard'}
                className={css(Styles.formControlFilters)}
                InputProps={{
                  ...params.InputProps,
                  disableUnderline: true,
                  readOnly: true,
                  classes: {
                    root: css([Styles.textInputStyles, Styles.autoCompleteInputRoot]),
                    input: css(Styles.formInputStyle, Styles.textInput),
                  },
                }} />}
          />
        </FormControl>
      </div>}
    </div>)
  }

  handleRefreshButtonClick () {
    this.fetchItems()
  }

  renderEntityDialog() {
    return (
      <PageDialog
        PageComponent={UserActivity2}
        PageComponentProps={this.state.entityDialogProps}
        title={this.state.entityDialogTitle}
        isOpen={this.state.isEntityDialogOpen}
        fullWidth={this.state.entityDialogFullWidth}
        fullHeight={this.state.entityDialogFullHeight}
        onClosed={() => {
          const dialogApiData = {
            responseBody: { id: this.state.entityId }
          }
          this.setState({ isEntityDialogOpen: false, dialogApiData, loadingMode: ApiHelper.LOADING_MODE.INSTANT_LOAD })
        }}
      />
    );
  }

  onSearch(searchText = '') {
    let filteredItems = this.state.statsStaffUserMasterList
    if(searchText) {
      filteredItems = StaffHelper.searchOnUserList(filteredItems, searchText)
    }

    this.setState({
      searchText: searchText,
      statsStaffUser: filteredItems
    })
  }

  clearSearch() {
    this.setState({
      searchText: '',
      timeFrame: this.timeFrame,
      teamSelected: this.team,
      interactionSelected: this.interaction,
    }, ()=>{
      this.reFetchReport({timeFrame: this.timeFrame, orgUnitId: this.orgId, interactionId: 'all'})
    })
  }

  renderSearchBar() {
    return(<div className={css(Styles.searchBarContainer)} onSubmit={(e) => e.preventDefault()}>
      <SearchBar
        hint={Helper.getString('staffSearchHint')}
        onSearch={this.onSearch.bind(this)}
        onClearSearch={this.clearSearch.bind(this)}
        searchText={this.state.searchText}
        searchButtonLabel='Filter'
        clearSearchLabel='Clear'
        autoRefresh={true}
        debounceDuration={1}
        autoFocus={true}
        hideFiltersContainer={true}
      >
      </SearchBar>
    </div>)
  }

  renderBelowTable() {
    if(this.state.customError && this.state.customError.errMsg) {
      const errorDetails = {
        image: require('../../assets/images/empty.svg'),
        message: this.state.customError.errMsg || Helper.getString('staffActivityReportNotAvailable'),
        buttonText: 'Refresh',
        action: this.handleRefreshButtonClick.bind(this),
      }

      return(
        <div className={css(Styles.outerContainer)}>
          <div className={css(Styles.reportDashboardContainer)}>
            {this.renderFilters()}
            <div className={css(Styles.reportsContainer, Styles.reportsDataContainer)}>
              {UiHelper.customErrorViewWithRefresh(errorDetails)}
            </div>
          </div>
        </div>
      )
    }

    const isInteractionFiltered = !!(this.state.interactionSelected && this.state.interactionSelected.key !== 'all')
    const report = this.state.statsStaffUser && this.state.statsActivity && generateStaffActivityReport({staffReport: this.state.statsStaffUser, stats: this.state.statsActivity, orgId: this.orgId, openUserActivityDialog: this.openUserActivityDialog.bind(this), workflows: this.workflows, isInteractionFiltered})
    let cellWidths = {}
    if(report && report.headers) {
      cellWidths  = Object.fromEntries(report.headers.map(header => [header, Styles.headerCell]))
    }

    return(
      <div className={css(Styles.outerContainer)}>
        <div className={css(Styles.reportDashboardContainer)}>
          <div className={css(Styles.reportsContainer, Styles.reportsDataContainer)}>
            {this.renderFilters()}
            {this.renderAggregatedStats()}
            {this.renderSearchBar()}
            <div className={css(Styles.tableStatItem)}>
              <TableCard
                item={{data: report || {},  maxRows: 100}}
                showMoreInTable={true}
                cellWidths={cellWidths}/>
            </div>
          </div>
        </div>
        {this.state.isEntityDialogOpen && this.renderEntityDialog()}
      </div>
    )
  }

  async customSubmitForMonthlyExport(options) {
    const { formType, formData, caller } = options
    delete formData.monthlyExport
    ApiHelper.callAwait({
      method: 'POST',
      endPoint: ApiHelper.makeUrlPath2({
        ms: AppConfig.CDP_MS.ENDPOINT,
        paths: ['miscs', 'export-staff-activity'],
        queryParams: {orgId: this.orgId},
      }),
      jsonBody: formData,
    }).then(response => {
      window.open(this.baseRoute() + '/jobscrm', '_self')
    }).catch(err => {
      const errorMessage = Helper.getErrorMsg(err);
      caller.setState({
        fetchState: ApiHelper.State.ERROR,
        errMsg: errorMessage,
        success: false,
        message: errorMessage,
        inProcess: false
      })
    })
  }

  async customSubmitForTableExport(options) {
    const { formType, formData, caller } = options
    delete formData.tableExport
    ApiHelper.callAwait({
      method: 'POST',
      endPoint: ApiHelper.makeUrlPath2({
        ms: AppConfig.CDP_MS.ENDPOINT,
        paths: ['miscs', 'export-staff-report'],
        queryParams: {orgId: this.orgId, ...(this.subOrgId && {subOrgId: this.subOrgId})},
      }),
      jsonBody: { orgUnitId: formData.orgUnitId, startTs: formData.startTs, endTs: formData.endTs }
    }).then(response => {
      window.open(this.baseRoute() + '/jobscrm', '_self')
    }).catch(err => {
      const errorMessage = Helper.getErrorMsg(err);
      caller.setState({
        fetchState: ApiHelper.State.ERROR,
        errMsg: errorMessage,
        success: false,
        message: errorMessage,
        inProcess: false
      })
    })
  }

  async customSubmit(options) {
    const { formType, formData, caller } = options
    if (formData.monthlyExport) { return this.customSubmitForMonthlyExport(options) }
    if (formData.tableExport) { return this.customSubmitForTableExport(options) }
    return caller.submit.call(caller, formData, true);
  }

}

export const Styles = StyleSheet.create({
  outerContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',

  },
  reportDashboardContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
    maxWidth: AppTheme.viewPort,
    overflow: 'hidden',
    marginBottom: 20,
    '@media (max-width: 1024px)': {
      gap: 0,
    }
  },
  reportsContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 10,
  },
  reportsDataContainer: {
    justifyContent: 'center',
    '@media (max-width: 1024px)': {
      maxWidth: '96vw'
    }
  },
  tableStatItem: {
    flex: 1,
    minWidth: '100%',
    height: '100%',
    maxWidth: window.innerWidth - AppTheme.drawerWidth - (AppTheme.adminPagePadding * 2),
    '@media (max-width: 767px)': {
      width: '100%',
      alignSelf: 'center',
      paddingLeft: '16px',
      paddingRight: '16px',
    },
  },
  infoCell: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
    gap: 2,
    fontSize: 16,
    minWidth: 0,
  },
  infoFieldTooltip: {
    display: 'block',
  },
  infoField: {
    display: 'block',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: 180,
  },
  numericStatItemRow: {
    '@media (max-width: 1024px)': {
      alignSelf: 'center',
      paddingTop: '8px',
      paddingBottom: '8px',
      height: '100%',
    },
    width: '100%',
  },
  numericStatContainerRow: {
    height: '100%',
    maxHeight: '100%',
    maxWidth: AppTheme.viewPort,
    textAlign: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    padding: '2px 0px 8px 0px',
    margin: 0,
    gap: 5,
    //justifyContent: 'left',
    '@media (max-width: 1024px)': {
      padding: '2px 0px',
      gap: 0
    },
  },
  statsCardContainer: {
    maxWidth: 'unset',
    flex: 1,
  },
  statsCard: {
    width: '100%'
  },
  filterContainer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: 10,
    '@media (max-width: 1024px)': {
      paddingTop: 5
    },
  },
  formControlContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
    gap: 5,
  },
  label: {
    fontSize: 14,
    color: CommonTheme.subTextColor,
    margin: 0,
    display: 'inline-block'
  },
  infoFieldLabel: {
    fontWeight: 500,
    display: 'inline-block',
    marginRight: 5,
  },
  formControlMainFilters: {
    width: 240,
    '@media (max-width: 1024px)': {
      width: '100%',
      alignSelf: 'center',
    },
  },
  formControlFilters: {
    borderRadius: AppTheme.borderRadius,
    border: '1px solid #E5E5E5',
    overflow: 'hidden'
  },
  textInputStyles: {
    padding: '11px 20px',
    flex: 1,
    textAlign: 'left',
    color: '#222222',
    fontSize: 16
  },
  autoCompleteInputRoot: {
    paddingRight: 48,
    '@media (max-width: 440px)': {
      paddingRight: 18,
    },
  },
  formInputStyle: {
    '::-webkit-scrollbar': {
      display: 'none'
    }
  },
  textInput: {
    padding: '4px 0px',
    '@media (max-width: 440px)': {
      fontSize: 12,
    },
  },
  displayFieldStyle: {
    '@media (max-width: 1024px)': {
      width: '100%',
    },
  },
  headerCell: {
    minWidth: 120,
  },
  cellBody: {
    backgroundColor: 'white',
    color: CommonTheme.textColor,
    // minHeight: 72,
    borderBottom: '1px solid ' + AppTheme.borderColor,
    fontSize: 16,
    textAlign: 'center',
    padding: '16px 5px',
    verticalAlign: 'middle',
    wordBreak: 'break-all',

    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: 200,
    minWidth: 0,
    '@media (max-width: 767px)': {
      fontSize: 10,
      padding: '16px 2px',
    },
  },
  firstColumn: {
    textAlign: 'left',
    whiteSpace: 'nowrap',
    // overflowX: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: 200,
    '@media(max-width: 767px)': {
      maxWidth: 'unset',
      whiteSpace: 'unset',
    }
  },
  spanValue: {
    fontSize: 14
  },
  tooltip: {
    fontSize: 12
  },
  cellWidthSmall: {
    width: '10%',
    padding: '8px 5px',
  },
  cellWidthNormal: {
    width: '20%',
    padding: '8px 5px',
  },
  cellWidthMedium: {
    width: '30%',
    padding: '8px 5px',
  },
  cellWidthWide: {
    width: '50%',
    padding: '8px 5px',
  },
  infoFieldHyperLink: {
    color: AppTheme.primaryColor,
    cursor: 'pointer'
  },
  searchBarContainer: {
    width: '100%'
  },
  stickyColumn: {
    position: 'sticky',
    left: 0,
    zIndex: 3,
    backgroundColor: 'white',
  },
})